/**
 * MailshotsReport — each sent mailshot and the quotes that followed it (Campaigns (beta) → Mailshots).
 *
 * Laid out like the Ads report: one row per mailshot, compared like with like. Each window (1 week; 1, 3 and 6 months) counts
 * from THAT mailshot's own send date, so an old and a new mailshot are compared over the same span. The large figure
 * is quotes per 1,000 homes mailed; the count is beneath. ⚠️ A window that has not yet passed is "—" with the days
 * to go, never 0. Lee's decisions of 1 Nov 2026; the figures are worked out by the server (getMailshotReport) and
 * the words by utils/mailshotReportWords.js.
 *
 * Every heading but the curve sorts the table (Lee, 2 Nov 2026), by the shared rules in utils/tableSort.js: a
 * second click flips the order, and a window not yet passed sorts last either way. Newest send first to begin with.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Panel } from '../Panel';
import Sparkline from '../Sparkline';
import InfoNotice from '../InfoNotice';
import InfoButton from '../InfoButton';
import { EmptyState } from '../EmptyState';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { nextSort, sortRows } from '../../../utils/tableSort';
import { WINDOW_LABELS, windowCell, soFarCell, sourceWords, leftOutLines, ABOUT, DEFAULT_SORT, sortAccessors, firstDirOf } from '../../../utils/mailshotReportWords';
import './MailshotsReport.scss';

const Cell = ({ cell, label }) => (
  <td className={`mailshots-report__num${cell.passed === false ? ' mailshots-report__num--open' : ''}`} data-label={label}>
    <strong>{cell.big}</strong>
    <small>{cell.small}</small>
  </td>
);
Cell.propTypes = { cell: PropTypes.object.isRequired, label: PropTypes.string.isRequired };

const ARIA_SORT = { asc: 'ascending', desc: 'descending' };
/** A heading that sorts the table: a button with an up/down mark, and aria-sort on the heading itself. */
const SortHead = ({ label, sortKey, sort, onSort, num, sortsBy }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === 'asc' ? FaSortUp : FaSortDown) : FaSort;
  return (
    <th scope="col" className={num ? 'mailshots-report__num' : undefined} aria-sort={active ? ARIA_SORT[sort.dir] : 'none'}>
      <button type="button" className={`mailshots-report__sort${active ? ' mailshots-report__sort--active' : ''}`} onClick={() => onSort(sortKey)} title={`Sort by ${sortsBy || label}`}>
        {label}
        <Icon className="mailshots-report__sort-icon" aria-hidden="true" />
      </button>
    </th>
  );
};
SortHead.propTypes = {
  label: PropTypes.string.isRequired, sortKey: PropTypes.string.isRequired, sort: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired, num: PropTypes.bool, sortsBy: PropTypes.string,
};

export default function MailshotsReport({ report, loading, error, color }) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  const keys = useMemo(() => ((report && report.windows) || []).map((w) => w.key), [report]);
  const rows = useMemo(
    () => sortRows((report && report.rows) || [], sort, sortAccessors(keys), (r) => r.name),
    [report, sort, keys],
  );
  const onSort = (key) => setSort((cur) => nextSort(cur, key, firstDirOf(key)));
  if (error) return <InfoNotice variant="error">The mailshot figures could not be loaded: {error}</InfoNotice>;
  if (loading && !report) return <p className="mailshots-report__loading">Loading the mailshots…</p>;
  if (!report) return null;
  const { totals = {} } = report;
  if (!rows.length) return <EmptyState title="No sent mailshots yet" description="A mailshot appears here once it is marked as mailed with a send date." />;
  const lines = leftOutLines(totals);
  return (
    <Panel
      title="Quotes after each mailshot"
      subtitle={`${totals.mailshots.toLocaleString('en-GB')} sent mailshots · ${totals.homes.toLocaleString('en-GB')} homes mailed · ${totals.quotes.toLocaleString('en-GB')} quotes. Figures are quotes per 1,000 homes mailed.`}
      actions={<InfoButton title="How these are counted">{ABOUT}</InfoButton>}
    >
      <div className="mailshots-report__scroll">
        <table className="mailshots-report">
          <thead>
            <tr>
              <SortHead label="Mailshot" sortsBy="send date" sortKey="mailed" sort={sort} onSort={onSort} />
              <SortHead label="Homes" sortKey="homes" sort={sort} onSort={onSort} num />
              {keys.map((k) => <SortHead key={k} label={WINDOW_LABELS[k] || k} sortKey={`w:${k}`} sort={sort} onSort={onSort} num />)}
              <SortHead label="So far" sortKey="soFar" sort={sort} onSort={onSort} num />
              <SortHead label="Bought the product" sortKey="bought" sort={sort} onSort={onSort} num />
              <SortHead label="Cancelled" sortKey="cancelled" sort={sort} onSort={onSort} num />
              <SortHead label="Median days" sortKey="median" sort={sort} onSort={onSort} num />
              <th scope="col">Quotes by week</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`mailshot-row-${r.id}`}>
                <th scope="row">
                  <span className="mailshots-report__name">{r.name}</span>
                  <small>sent {r.mailedDay} · {sourceWords(r)}</small>
                </th>
                <td className="mailshots-report__num" data-label="Homes">{r.homes.toLocaleString('en-GB')}</td>
                {keys.map((k) => <Cell key={k} cell={windowCell(r.windows[k])} label={WINDOW_LABELS[k] || k} />)}
                <Cell cell={{ passed: true, ...soFarCell(r) }} label="So far" />
                <td className="mailshots-report__num" data-label="Bought the product">{r.bought.toLocaleString('en-GB')}</td>
                <td className="mailshots-report__num" data-label="Cancelled">{r.cancelled.toLocaleString('en-GB')}</td>
                <td className="mailshots-report__num" data-label="Median days">{r.medianDays == null ? '—' : r.medianDays}</td>
                <td data-label="Quotes by week">
                  <Sparkline data={r.curve} color={color} width={110} height={26} title={`Quotes so far, week by week since ${r.mailedDay}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lines.length > 0 && (
        <ul className="mailshots-report__notes">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
      )}
    </Panel>
  );
}

MailshotsReport.propTypes = {
  report: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  color: PropTypes.string,
};
